<template>
<div class="page-home">
  <!-- The point of this wrapper is to make the first two blocks have aggregated height of at least 100vh if the hero
  promo block is to be displayed. -->
  <div class="two-top-blocks" :class="{ 'two-top-blocks--full-height': displayHeroPromo }">
    <component v-if="displayHeroPromo" :is="homePromoComponent" :promo="settings.home_promo" />
    <Block v-if="blocks[0]" :descriptor="blocks[0]" />
  </div>

  <Block v-for="block of blocks.slice(1)" :key="block.is.name" :descriptor="block" />
</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import isScreenConditionTrue from '@/helpers/isScreenConditionTrue'
import InjectsSeo from '@/mixins/InjectsSeo'

const Block = ({ props }) => {
  const BlockComponent = props.descriptor.is;
  return (
    <section
      class="home-page-section"
      { ...props.descriptor.wrapperAttrs }
    >
      <BlockComponent
        { ...props.descriptor.componentAttrs }
      />
    </section>
  );
};

export default {
  mixins: [InjectsSeo],

  components: {
    Block,
    FeatureColumns: () => import('./../components/features/FeatureColumns'),
    OffersCards: () => import('./../components/offers/OffersCards'),
    OffersFullWidth: () => import('./../components/offers/OffersFullWidth'),
    Categories: () => import('@/components/CategoriesHomeBlock'),
    FeedbackForm: () => import('./../components/feedback/FeedbackForm'),
    ProductsCollection: () => import('@/components/products-collections/ProductsCollection'),
  },

  metaInfo() {
    return this.getMetaInfoFromSettings(this.homeSeo)
  },

  mounted() {
    this.$root.sharedEventBus.$on('designer:highlight:apply', namespace => {
      if (this.highlightedNamespaces.indexOf(namespace) === -1) {
        return
      }

      this.highlightedSections[namespace] = true
    })

    this.$root.sharedEventBus.$on('designer:highlight:remove', namespace => {
      if (this.highlightedNamespaces.indexOf(namespace) === -1) {
        return
      }

      this.highlightedSections[namespace] = false
    })
  },

  computed: {
    ...mapState({
      settings: state => state.settings,
    }),
    ...mapGetters({
      offersLoaded: 'offers/loaded',
      hasOffers: 'offers/hasOffers',
      feedback: 'settings/homeFeedback',
      homeSeo: 'settings/homeSeo',
    }),

    displayHeroPromo() {
      return this.mustDisplayBlock(this.settings.home_promo.visibility);
    },

    homePromoComponent() {
      return () => import('@/components/home-promos/HomePageHeroPromo');
    },

    offersComponent() {
      switch (this.$store.state.settings.home_offers.variant) {
        case 'cards': return this.$options.components.OffersCards;
        case 'fullWidth': return this.$options.components.OffersFullWidth;
        default: throw new Error('Unexpected offers variant name');
      }
    },

    shouldDisplayOffersSection() {
      return this.mustDisplayBlock(this.settings.home_offers.visibility) && this.offersLoaded && this.hasOffers;
    },

    shouldDisplayProductsCollectionSection() {
      const blockSettings = this.settings.home_products_collection;
      return this.mustDisplayBlock(blockSettings.visibility) && !!blockSettings.collection_id;
    },

    blocks() {
      const blocksUnsorted = [];

      // Push blocks to the array if they are to be displayed.
      if (this.mustDisplayBlock(this.settings.home_features.visibility)) {
        blocksUnsorted.push({
          is: this.$options.components.FeatureColumns,
          wrapperAttrs: { class: `section-home-features${this.highlightedSections.home_features ? ' designer-section-highlighted' : ''}` },
          order: this.settings.home_features.order,
        });
      }

      if (this.shouldDisplayOffersSection) {
        blocksUnsorted.push({
          is: this.offersComponent,
          wrapperAttrs: { class: `section-home-offers${this.highlightedSections.home_offers ? ' designer-section-highlighted' : ''}` },
          order: this.settings.home_offers.order,
        });
      }

      if (this.mustDisplayBlock(this.settings.home_categories.visibility)) {
        blocksUnsorted.push({
          is: this.$options.components.Categories,
          wrapperAttrs: { class: `${this.highlightedSections.home_categories ? 'designer-section-highlighted' : ''}` },
          order: this.settings.home_categories.order,
        });
      }

      if (this.shouldDisplayProductsCollectionSection) {
        const blockSettings = this.$store.state.settings.home_products_collection;
        blocksUnsorted.push({
          is: this.$options.components.ProductsCollection,
          componentAttrs: {
            props: {
              collectionId: blockSettings.collection_id,
              title: blockSettings.title,
              count: parseInt(blockSettings.count, 10) || -1,
            },
          },
          wrapperAttrs: { class: `section-home-products-collection${this.highlightedSections.home_products_collection ? ' designer-section-highlighted' : ''}` },
          order: this.settings.home_products_collection.order,
        });
      }

      if (this.mustDisplayBlock(this.feedback.visibility)) {
        blocksUnsorted.push({
          is: this.$options.components.FeedbackForm,
          componentAttrs: {
            props: {
              title: this.feedback.title,
              content: this.feedback.content,
              label: this.feedback.label,
            },
          },
          wrapperAttrs: { class: `section-home-feedback container-padded${this.highlightedSections.home_feedback ? ' designer-section-highlighted' : ''}` },
          order: this.settings.home_feedback.order,
        });
      }

      return blocksUnsorted.sort((a, b) => a.order - b.order);
    },
  },

  methods: {
    mustDisplayBlock(displaySettingVal) {
      return isScreenConditionTrue(this.$store, displaySettingVal);
    },
  },

  data() {
    return {
      highlightedNamespaces: [
        'home_promo', 'home_features', 'home_feedback',
        'home_offers', 'home_products_collection', 'home_categories',
      ],

      highlightedSections: {
        home_promo: false,
        home_features: false,
        home_offers: false,
        home_products_collection: false,
        home_categories: false,
        home_feedback: false,
      },
    }
  }
}
</script>

<style lang="less">
.two-top-blocks {
  &--full-height {
    @apply min-h-screen flex flex-col;
    margin-top: calc(-1 * var(--header-height));
    .section-home-hero-promo {
      @apply flex-grow;
    }
  }
}

.home-page-section {
  &.section-home-feedback,
  &.section-home-products-collection {
    @apply my-6;
  }
  &.section-home-features {
    @apply my-4;
  }
  &.section-home-features {
    @apply pt-8;
  }
}

.section-home-feedback {
  @apply max-w-md;
}
</style>
